import { EVENT_TYPES, TypeIdSchema } from '@unipile/db';
import { Type, type Static } from '@sinclair/typebox';
import { StringEnum } from '../../utils/typebox';

const WebhookEndpointUrlSchema = Type.String({
  description: 'The HTTP or HTTPS URL to send the webhook payload to.',
  format: 'uri',
  minLength: 1,
  maxLength: 255,
  pattern: '^https?://',
});

export const UpdateWebhookEndpointParamsSchema = Type.Object({
  id: TypeIdSchema('webhookEndpoint', { description: 'The ID of the Webhook Endpoint to update.' }),
});

export const UpdateWebhookEndpointBodySchema = Type.Object(
  {
    trigger_events: Type.Optional(
      Type.Array(StringEnum([...EVENT_TYPES]), {
        description: `The events that will trigger the webhook endpoint.
          Refer to [Events Types](https://developer.unipile.com/v2.0/reference/event-types-1) to see the list of available values.`,
      }),
    ),
    account_ids: Type.Optional(
      Type.Array(TypeIdSchema('account'), {
        description:
          'Restrict the webhook to specific accounts. Leave empty or omit the field to listen to events from every account in the application.',
      }),
    ),
    url: Type.Optional(WebhookEndpointUrlSchema),
    description: Type.Optional(Type.String({ description: 'A description of the webhook endpoint.', maxLength: 255 })),
    enabled: Type.Optional(Type.Boolean({ description: 'Whether the webhook endpoint is enabled.' })),
  },
  { additionalProperties: false },
);

export const CreateWebhookEndpointBodySchema = Type.Object({
  trigger_events: Type.Array(StringEnum([...EVENT_TYPES]), {
    description: `The events that will trigger the webhook endpoint.
      Refer to [Events Types](https://developer.unipile.com/v2.0/reference/event-types-1) to see the list of available values.`,
  }),
  account_ids: Type.Optional(
    Type.Array(TypeIdSchema('account'), {
      description:
        'Restrict the webhook to specific accounts. Leave empty or omit the field to listen to events from every account in the application.',
    }),
  ),
  url: WebhookEndpointUrlSchema,
  description: Type.Optional(Type.String({ description: 'A description of the webhook endpoint.', maxLength: 255 })),
});

export const DeleteWebhookEndpointParamsSchema = Type.Object({
  id: TypeIdSchema('webhookEndpoint', { description: 'The ID of the Webhook Endpoint to delete.' }),
});

export const GetWebhookEndpointParamsSchema = Type.Object({
  id: TypeIdSchema('webhookEndpoint', { description: 'The ID of the Webhook Endpoint to retrieve.' }),
});

export const ListWebhookEndpointsQuerySchema = Type.Object({
  offset: Type.Optional(Type.String({ description: 'An offset used for pagination.' })),
  limit: Type.Optional(Type.String({ description: 'The limit of items to be returned.', default: '20' })),
});

export type UpdateWebhookEndpointBody = Static<typeof UpdateWebhookEndpointBodySchema>;
export type CreateWebhookEndpointBody = Static<typeof CreateWebhookEndpointBodySchema>;
export type ListWebhookEndpointsQuery = Static<typeof ListWebhookEndpointsQuerySchema>;
